import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';

/**
 * Bridge natif @capacitor-firebase/messaging enregistré via `registerPlugin` de
 * @capacitor/core : proxy SYNCHRONE, aucun chunk web à charger, aucune dépendance
 * au SDK JS `firebase`. On NE fait PLUS `import('@capacitor-firebase/messaging')` :
 * dans le WKWebView iOS cet import dynamique restait bloqué (ni resolve ni reject),
 * il tirait le chunk web du plugin → le hook restait « loading ». Cf.
 * `use-push-notifications.ts` et FIREBASE_SETUP.md (étape 6b).
 *
 * Module partagé par le hook (permissions/token) ET `main.tsx` (écoute globale du
 * tap sur notification → deep link), pour ne déclarer les surcharges d'events
 * qu'une seule fois.
 */
export interface FirebaseMessagingBridge {
  checkPermissions(): Promise<{ receive: string }>;
  requestPermissions(): Promise<{ receive: string }>;
  getToken(): Promise<{ token: string }>;
  deleteToken(): Promise<void>;
  /** Rotation du token FCM (réinstall, restore, expiration). */
  addListener(
    eventName: 'tokenReceived',
    listenerFunc: (event: { token: string }) => void,
  ): Promise<PluginListenerHandle>;
  /**
   * Une notification ARRIVE pendant que l'app est au premier plan (aucun tap).
   * Sert de signal « temps réel » : le serveur envoie déjà un push pour chaque
   * nouveauté du Feed, on recharge le fil là-dessus plutôt que d'ouvrir un
   * abonnement Realtime par table.
   */
  addListener(
    eventName: 'notificationReceived',
    listenerFunc: (event: {
      notification?: { data?: Record<string, unknown> | null } | null;
    }) => void,
  ): Promise<PluginListenerHandle>;
  /** L'utilisateur a TAPÉ la notification → on lit `notification.data.url`. */
  addListener(
    eventName: 'notificationActionPerformed',
    listenerFunc: (event: {
      actionId?: string;
      notification?: { data?: Record<string, unknown> | null };
    }) => void,
  ): Promise<PluginListenerHandle>;
}

export const FirebaseMessaging = registerPlugin<FirebaseMessagingBridge>('FirebaseMessaging');
